import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  East as EastIcon,
  South as SouthIcon,
  West as WestIcon,
  North as NorthIcon,
  ArrowForward as ArrowIcon,
  Timeline as TimelineIcon,
  CheckCircle as ConfirmedIcon,
  Star as StarIcon,
  Lock as LockIcon,
  NavigateBefore,
  NavigateNext,
  CalendarMonth as CalendarIcon,
  TrendingUp as ProgressionIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { hasPremiumSubscription } from '../utils/subscription';
import NorthIndianChartRenderer, {
  NORTH_INDIAN_HOUSE_POSITIONS,
  NorthIndianHouseLabel,
} from '../components/charts/NorthIndianChartRenderer';
import NorthIndianChart, { EastIndianChartRenderer, SouthIndianChartRenderer } from '../components/charts/NorthIndianChart';
import { useChart } from '../contexts/ChartContext';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import AnalysisTabBar from '../components/AnalysisTabBar';
import SeoHead from '../components/seo/SeoHead';
import { getPlanetAbbr, getSignAbbr } from '../i18n/astrology';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';
const toLocalYmd = (date: Date) => (
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
);

const parseYmdLocal = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
};

const BNN_TRANSIT_TAB_META = [
  {
    title: 'BNN Analysis - Transit Timeline',
    description: 'Inspect upcoming BNN transit events and projected activation windows for this chart.'
  },
  {
    title: 'BNN Analysis - Transit Activations',
    description: 'Review active BNN transit triggers and directional activation patterns for this chart.'
  },
  {
    title: 'BNN Analysis - Directional Transit Chart',
    description: 'View the BNN directional transit chart with live planetary movement and directional emphasis.'
  }
];

// ─── Types ───────────────────────────────────────────────────────────────────

interface BNNPlanet {
  key: string;
  name: string;
  sign: number;
  longitude: number;
  degInSign: number;
  isRetro: boolean;
  isVirtual?: boolean;
  note?: string;
  /** @deprecated use isVirtual */
  retroInPrev?: boolean;
}

interface DirectionData {
  signs: number[];
  element: string;
  label: string;
  planets: BNNPlanet[];
}

interface NadiString {
  direction: string;
  planets: string[];
  keys: string[];
  interpretation: { en: string; hi: string };
}

interface KarakaString {
  karaka: string;
  karakaName: string;
  planet: string;
  planetName: string;
  relationship: string;
  interpretation: { en: string; hi: string };
}

interface DegreeFlowArrow {
  from: string;
  to: string;
  label: string;
}

interface ParivartanaPair {
  planetA: string;
  planetAName: string;
  planetASign: number;
  planetB: string;
  planetBName: string;
  planetBSign: number;
  note: string;
}

interface BhriguPoint {
  name: string;
  longitude: number;
  sign: string;
  signNumber?: number;
  degreeInSign: number;
}

interface BNNResult {
  selfKaraka: { key: string; name: string };
  bhriguPoint?: BhriguPoint | null;
  planets: BNNPlanet[];
  directionalGroups: Record<string, DirectionData>;
  degreeFlow: Record<string, DegreeFlowArrow[]>;
  parivartana: ParivartanaPair[];
  directionalAspects: { type: string; from: string; to: string; label: string }[];
  nadiStrings: NadiString[];
  karakaStrings: KarakaString[];
}

interface BNNLocalizedText {
  en: string;
  hi: string;
}

interface BNNGuidanceConnection {
  planet: string;
  planetName: string;
  relativeHouse: number;
  connectionClass: string;
  score: number;
  reason: string;
  interpretation: BNNLocalizedText;
}

interface BNNGuidance {
  concrete?: {
    career: BNNConcreteFieldResult;
    education: BNNConcreteFieldResult;
    marriage: BNNConcreteTimedResult;
    children: BNNConcreteTimedResult;
    foreign: BNNConcreteTimedResult;
    wellness: BNNConcreteWellnessResult;
  };
  career: {
    primaryFields: { field: BNNLocalizedText; score: number; confidence: string; reasons: BNNLocalizedText[] }[];
    workStyle: BNNLocalizedText[];
    cautions: BNNLocalizedText[];
    ruleTrace: string[];
  };
  topics: {
    topic: string;
    karaka: string;
    karakaName: string;
    score: number;
    strength: string;
    summary: BNNLocalizedText;
    topConnections: BNNGuidanceConnection[];
  }[];
  eventGuidance: {
    topic: string;
    confidence: string;
    headline: BNNLocalizedText;
    detail: BNNLocalizedText;
    transitTriggers: BNNLocalizedText[];
    ruleTrace: string[];
  }[];
  disclaimer: BNNLocalizedText;
}

interface BNNConcreteItem {
  value: string;
  label?: BNNLocalizedText;
  score: number;
  reasons: BNNLocalizedText[];
}

interface BNNConcreteFieldResult {
  title: BNNLocalizedText;
  confidence: string;
  score: number;
  result: BNNLocalizedText;
  suitedFor: BNNConcreteItem[];
  reasons: BNNLocalizedText[];
}

interface BNNYearWindow {
  startAge: number;
  endAge: number;
  startYear: number | null;
  endYear: number | null;
  score: number;
  confidence: string;
  reasons: BNNLocalizedText[];
}

interface BNNConcreteTimedResult {
  title: BNNLocalizedText;
  result: BNNLocalizedText;
  windows: BNNYearWindow[];
  nature?: BNNLocalizedText[];
  reasons: BNNLocalizedText[];
}

interface BNNConcreteWellnessResult {
  title: BNNLocalizedText;
  result: BNNLocalizedText;
  cautionYears: BNNYearWindow[];
  watchAreas: BNNLocalizedText[];
  reasons: BNNLocalizedText[];
}

// ─── Transit Types ────────────────────────────────────────────────────────────

interface TransitInterpretation {
  planet: string;
  name: string;
  en: string;
  hi: string;
}

interface TransitActivatedPlanet {
  key: string;
  name: string;
  sign: number;
  degInSign: number;
  status?: 'Starting' | 'Fructifying';
}

interface TransitTrigger {
  transitPlanet: string;
  transitSign: number;
  direction: string;
  trineActivated: TransitActivatedPlanet[];
  destinationSign: number;
  destinationDir: string;
  destActivated: TransitActivatedPlanet[];
  backSign: number;
  backDir: string;
  backActivated: TransitActivatedPlanet[];
  oppSign: number;
  oppDir: string;
  oppActivated: TransitActivatedPlanet[];
  activatesSelf: boolean;
  interpretations: TransitInterpretation[];
  doubleTransit?: boolean;
  doubleTransitPlanets?: string[];
}

interface TimelineEntry {
  year: number;
  label?: string;
  periodStart?: string;
  periodEnd?: string;
  jupiterSign: number;
  jupiterDir: string;
  saturnSign: number;
  saturnDir: string;
  jupiterMajorSign?: number;
  jupiterMajorDir?: string;
  jupiterMinorSign?: number;
  jupiterMinorDir?: string;
  saturnMajorSign?: number;
  saturnMajorDir?: string;
  saturnMinorSign?: number;
  saturnMinorDir?: string;
  activatedByJupiter: string[];
  activatedBySaturn: string[];
  isDoubleTransit: boolean;
  selfActivated: boolean;
  intensity: 'Very High' | 'High' | 'Medium' | 'Low';
  event: string;
  hinglish: string;
}

interface TransitResult {
  transitDate: string;
  transitSignMap: Record<string, number>;
  currentTriggers: TransitTrigger[];
  timeline: TimelineEntry[];
}

// ─── Progression Types ────────────────────────────────────────────────────────

interface ProgressionCycle {
  planet: string;
  sign: number;
  signName: string;
  direction: string;
  type?: 'major' | 'minor';
  degree?: number;
  year?: number;
  month?: number;
  activated: { key: string; name: string }[];
}

interface ProgressionPoint {
  planet: string;
  type: 'major' | 'minor';
  sign: number;
  signName: string;
  direction: string;
  degree: number;
  month?: number;
  periodStart?: string;
  periodEnd?: string;
}

interface ProgressionTableRow {
  planet: string;
  sign: number;
  signName: string;
  direction: string;
  from: string;
  to: string;
  durationYears?: number;
  isBalancePeriod?: boolean;
  majorSign?: number;
  majorSignName?: string;
  majorFrom?: string;
  majorTo?: string;
  minorIndex?: number;
}

interface ProgressionTables {
  major?: ProgressionTableRow[];
  minor?: ProgressionTableRow[];
}

type PlanetProgressions = Record<string, {
  major?: ProgressionPoint;
  minor?: ProgressionPoint;
}>;

interface RelationalHouse {
  house: number;
  sign: number;
  signName: string;
  direction: string;
  planets: { key: string; name: string; degInSign: number; isRetro: boolean }[];
}

interface ProgressionResult {
  targetDate: string;
  yearsDiff: number;
  monthsDiff: number;
  major: ProgressionCycle;
  minor: ProgressionCycle;
  progressions?: PlanetProgressions;
  jeevaSign: number;
  karmaSign: number;
  jeevaHouses: RelationalHouse[];
  karmaHouses: RelationalHouse[];
  progressionTables?: ProgressionTables;
  hinglish: string;
}

// ─── Direction config ─────────────────────────────────────────────────────────

const DIR_CONFIG: Record<string, { color: string; bg: string; Icon: React.ElementType }> = {
  East:  { color: '#e65100', bg: '#fff3e0', Icon: EastIcon  },
  South: { color: '#2e7d32', bg: '#e8f5e9', Icon: SouthIcon },
  West:  { color: '#1565c0', bg: '#e3f2fd', Icon: WestIcon  },
  North: { color: '#6a1b9a', bg: '#f3e5f5', Icon: NorthIcon },
};

const textKey = (value: string) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
const planetText = (t: any, name: string) => t(`astrology.planets.${String(name || '').trim().toUpperCase()}`, { defaultValue: name || '-' });
const directionText = (t: any, direction: string) => t(`bnn.directions.${direction}`, { defaultValue: direction || '-' });
const elementText = (t: any, element: string) => t(`bnn.elements.${element}`, { defaultValue: element || '-' });
const yesNoRetro = (t: any, isRetro: boolean) => (isRetro ? t('bnn.retroYes') : '—');
const translateFreeText = (t: any, value: string) => t(`bnn.text.${textKey(value)}`, { defaultValue: value || '-' });

const SIGN_NAMES: Record<number, string> = {
  1: 'Aries', 2: 'Taurus', 3: 'Gemini', 4: 'Cancer',
  5: 'Leo',   6: 'Virgo',  7: 'Libra',  8: 'Scorpio',
  9: 'Sagittarius', 10: 'Capricorn', 11: 'Aquarius', 12: 'Pisces',
};
// ─── Transit Section (tabbed) ──────────────────────────────────────────────────────────────

const INTENSITY_COLOR: Record<string, string> = {
  'Very High': '#b71c1c',
  'High':      '#e65100',
  'Medium':    '#f57f17',
  'Low':       '#388e3c',
};

const PROGRESSION_COLORS: Record<string, string> = {
  Jupiter: '#F59E0B',
  Saturn: '#64748B',
  Rahu: '#8B5CF6',
  Ketu: '#EF4444',
};

const PROGRESSION_LABELS: Record<string, string> = {
  Jupiter: 'Ju',
  Saturn: 'Sa',
  Rahu: 'Ra',
  Ketu: 'Ke',
};

// — helper: which direction does a sign belong to?
function signDir(s: number): string {
  if ([1,5,9].includes(s))  return 'East';
  if ([2,6,10].includes(s)) return 'South';
  if ([3,7,11].includes(s)) return 'West';
  return 'North';
}

// ─── Sub-tab 1: Activations Table ──────────────────────────────────────────

const ActivationsTable: React.FC<{
  triggers: TransitTrigger[];
  selfKaraka: { key: string; name: string };
  transitSignMap: Record<string, number>;
}> = ({ triggers, selfKaraka, transitSignMap }) => {
  const { t } = useTranslation();
  return (
  <TableContainer component={Paper} sx={{ borderRadius: 2, mt: 1 }}>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ bgcolor: '#1a1a2e' }}>
          <TableCell sx={{ color: '#f9a825', fontWeight: 600 }}>{t('bnn.transitPlanet')}</TableCell>
          <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('bnn.signDirection')}</TableCell>
          <TableCell sx={{ color: '#81c784', fontWeight: 600 }}>{t('bnn.trine')}</TableCell>
          <TableCell sx={{ color: '#64b5f6', fontWeight: 600 }}>{t('bnn.secondFront')}</TableCell>
          <TableCell sx={{ color: '#ce93d8', fontWeight: 600 }}>{t('bnn.twelfthBack')}</TableCell>
          <TableCell sx={{ color: '#ef9a9a', fontWeight: 600 }}>{t('bnn.seventhOpp')}</TableCell>
          <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('bnn.interpretationHinglish')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {triggers.map((trigger, i) => {
          const cfg = DIR_CONFIG[trigger.direction];
          const deg = transitSignMap[`${trigger.transitPlanet}_DEG`];
          return (
            <TableRow
              key={i}
              sx={{
                bgcolor: trigger.doubleTransit ? '#fffde7' : i % 2 === 0 ? '#fafafa' : 'white',
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              <TableCell>
                <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                  {trigger.doubleTransit && <ConfirmedIcon sx={{ fontSize:14, color:'#f9a825' }} />}
                  <Typography variant="body2" sx={{ fontWeight: 600, color: cfg?.color }}>
                    {planetText(t, trigger.transitPlanet)}
                  </Typography>
                  {trigger.activatesSelf && (
                    <Chip label={planetText(t, selfKaraka.name)} size="small"
                      sx={{ height:14, fontSize:'0.55rem', bgcolor:'#f9a825', color:'#000', fontWeight: 600 }}
                    />
                  )}
                </Box>
                {deg !== undefined && (
                  <Typography variant="caption" sx={{ color:'text.secondary', fontFamily:'monospace' }}>
                    {getSignAbbr(t, trigger.transitSign)} {Number(deg).toFixed(1)}°
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Chip
                  label={`${trigger.transitSign} • ${directionText(t, trigger.direction)}`}
                  size="small"
                  sx={{ bgcolor:`${cfg?.color}18`, color:cfg?.color, fontWeight:600, fontSize:'0.65rem' }}
                />
              </TableCell>
              {/* Trine */}
              <TableCell>
                {trigger.trineActivated.length === 0 ? <Typography variant="caption" color="text.disabled">—</Typography> : (
                  <Box sx={{ display:'flex', flexDirection:'column', gap:0.25 }}>
                    {trigger.trineActivated.map(p => (
                      <Box key={p.key} sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                        <Chip label={planetText(t, p.name)} size="small" variant="outlined"
                          sx={{ fontSize:'0.65rem', height:18, borderColor: cfg?.color, color: cfg?.color }}
                        />
                        {p.status && (
                          <Chip
                            label={t(`bnn.status.${p.status}`, { defaultValue: p.status })}
                            size="small"
                            sx={{
                              height:14, fontSize:'0.5rem', fontWeight: 600,
                              bgcolor: p.status === 'Fructifying' ? '#c8e6c9' : '#fff9c4',
                              color:   p.status === 'Fructifying' ? '#1b5e20' : '#f57f17',
                            }}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </TableCell>
              {/* 2nd Front */}
              <TableCell>
                {trigger.destActivated.length === 0
                  ? <Typography variant="caption" color="text.disabled">—</Typography>
                  : trigger.destActivated.map(p => (
                    <Chip key={p.key} label={planetText(t, p.name)} size="small"
                      sx={{ fontSize:'0.65rem', height:18, mr:0.25, bgcolor:'#e3f2fd' }}
                    />
                  ))
                }
              </TableCell>
              {/* 12th Back */}
              <TableCell>
                {trigger.backActivated.length === 0
                  ? <Typography variant="caption" color="text.disabled">—</Typography>
                  : trigger.backActivated.map(p => (
                    <Chip key={p.key} label={planetText(t, p.name)} size="small"
                      sx={{ fontSize:'0.65rem', height:18, mr:0.25, bgcolor:'#f3e5f5' }}
                    />
                  ))
                }
              </TableCell>
              {/* 7th Opp */}
              <TableCell>
                {trigger.oppActivated.length === 0
                  ? <Typography variant="caption" color="text.disabled">—</Typography>
                  : trigger.oppActivated.map(p => (
                    <Chip key={p.key} label={planetText(t, p.name)} size="small"
                      sx={{ fontSize:'0.65rem', height:18, mr:0.25, bgcolor:'#ffebee' }}
                    />
                  ))
                }
              </TableCell>
              {/* Hinglish */}
              <TableCell sx={{ maxWidth:200 }}>
                {trigger.interpretations.slice(0,2).map((interp, j) => (
                  <Tooltip key={j} title={translateFreeText(t, interp.en)} arrow>
                    <Typography variant="caption" sx={{ display:'block', fontStyle:'italic', color:'text.secondary', cursor:'help',
                      overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:190 }}>
                      {translateFreeText(t, interp.hi)}
                    </Typography>
                  </Tooltip>
                ))}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
  );
};

// ─── Sub-tab 2: Timeline Table ────────────────────────────────────────────

const TimelineTable: React.FC<{ timeline: TimelineEntry[] }> = ({ timeline }) => {
  const { t } = useTranslation();
  const renderProgressionChips = (majorSign: number, majorDir: string, minorSign?: number, minorDir?: string) => (
    <Stack spacing={0.5} alignItems="flex-start">
      <Chip label={`Major: ${getSignAbbr(t, majorSign)} (${directionText(t, majorDir)?.charAt(0) ?? '?'})`}
        size="small"
        sx={{ bgcolor:`${DIR_CONFIG[majorDir]?.color}18`, color:DIR_CONFIG[majorDir]?.color||'text.primary', fontWeight:600, fontSize:'0.65rem' }}
      />
      {minorSign && minorDir && (
        <Chip label={`Minor: ${getSignAbbr(t, minorSign)} (${directionText(t, minorDir)?.charAt(0) ?? '?'})`}
          size="small"
          variant="outlined"
          sx={{ borderColor:`${DIR_CONFIG[minorDir]?.color}66`, color:DIR_CONFIG[minorDir]?.color||'text.primary', fontWeight:600, fontSize:'0.65rem' }}
        />
      )}
    </Stack>
  );
  return (
  <TableContainer component={Paper} sx={{ borderRadius: 2, mt: 1 }}>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ bgcolor: '#1a1a2e' }}>
          <TableCell sx={{ color: '#f9a825', fontWeight: 600 }}>{t('bnn.year')}</TableCell>
          <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('bnn.event')}</TableCell>
          <TableCell sx={{ color: '#81c784', fontWeight: 600 }}>{planetText(t, 'Jupiter')}</TableCell>
          <TableCell sx={{ color: '#ce93d8', fontWeight: 600 }}>{planetText(t, 'Saturn')}</TableCell>
          <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('bnn.intensity')}</TableCell>
          <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('bnn.interpretationHinglish')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {timeline.map((entry, i) => (
          <TableRow
            key={i}
            sx={{
              bgcolor: entry.isDoubleTransit ? '#fffde7'
                : entry.selfActivated ? '#f3e5f5'
                : i % 2 === 0 ? '#fafafa' : 'white',
            }}
          >
            <TableCell sx={{ fontWeight: 600, fontSize:'0.95rem' }}>
              <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                {entry.isDoubleTransit && <StarIcon sx={{ fontSize:14, color:'#f9a825' }} />}
                {entry.label || entry.year}
              </Box>
            </TableCell>
            <TableCell sx={{ fontWeight: entry.isDoubleTransit ? 700 : 400, fontSize:'0.8rem' }}>
              {translateFreeText(t, entry.event)}
            </TableCell>
            <TableCell>
              {renderProgressionChips(
                entry.jupiterMajorSign || entry.jupiterSign,
                entry.jupiterMajorDir || entry.jupiterDir,
                entry.jupiterMinorSign,
                entry.jupiterMinorDir
              )}
            </TableCell>
            <TableCell>
              {renderProgressionChips(
                entry.saturnMajorSign || entry.saturnSign,
                entry.saturnMajorDir || entry.saturnDir,
                entry.saturnMinorSign,
                entry.saturnMinorDir
              )}
            </TableCell>
            <TableCell>
              <Chip label={t(`bnn.intensities.${entry.intensity}`, { defaultValue: entry.intensity })} size="small"
                sx={{ bgcolor:`${INTENSITY_COLOR[entry.intensity]}18`, color:INTENSITY_COLOR[entry.intensity], fontWeight: 600, fontSize:'0.65rem',
                  border: entry.isDoubleTransit ? `1.5px solid ${INTENSITY_COLOR[entry.intensity]}` : 'none' }}
              />
            </TableCell>
            <TableCell sx={{ maxWidth:220 }}>
              <Tooltip title={translateFreeText(t, entry.hinglish)} arrow>
                <Typography variant="caption" sx={{ display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical',
                  overflow:'hidden', cursor:'help', color:'text.secondary', fontStyle:'italic' }}>
                  {translateFreeText(t, entry.hinglish)}
                </Typography>
              </Tooltip>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
  );
};

// ─── Sub-tab 3: Directional Chart (Natal + Transit overlay) ──────────────────

const DirectionalTransitChart: React.FC<{
  bnn: BNNResult;
  transitSignMap: Record<string, number>;
  includeRetrogradeCorrections?: boolean;
  title?: string;
  referenceChartData?: any;
}> = ({ bnn, transitSignMap, includeRetrogradeCorrections = false, title = 'Planet Directions', referenceChartData }) => {
  const { t } = useTranslation();
  const transitPlanets = Object.entries(transitSignMap)
    .filter(([k]) => !k.includes('_DEG'))
    .map(([k, sign]) => ({ key: k, sign, dir: signDir(sign) }));
  const chartDirections = ['North', 'East', 'West', 'South'] as const;
  const quadrantMap: Record<string, { top: string; left: string; align: 'left' | 'right' | 'center'; label: string }> = {
    North: { top: '16%', left: '19%', align: 'center', label: 'Karma' },
    East: { top: '16%', left: '63%', align: 'center', label: 'Dharma' },
    West: { top: '58%', left: '19%', align: 'center', label: 'Artha' },
    South: { top: '58%', left: '63%', align: 'center', label: 'Moksha' },
  };

  const getNatalPlanets = (dir: string) => (
    (bnn.directionalGroups[dir]?.planets ?? []).filter(p => (
      includeRetrogradeCorrections ? true : (!p.isVirtual && !p.retroInPrev)
    ))
  );

  const getTransitPlanets = (dir: string) => transitPlanets.filter(p => p.dir === dir);
  const positionRows = chartDirections.flatMap((dir) => {
    const natal = getNatalPlanets(dir).map((planet) => ({
      id: `${dir}-natal-${planet.key}`,
      type: t('bnn.natal'),
      direction: dir,
      planet: planetText(t, planet.name),
      sign: getSignAbbr(t, planet.sign),
      degree: `${planet.degInSign.toFixed(2)}°`,
      color: DIR_CONFIG[dir].color,
    }));

    const transits = getTransitPlanets(dir).map((planet) => {
      const deg = transitSignMap[`${planet.key}_DEG`];
      return {
        id: `${dir}-transit-${planet.key}`,
        type: t('bnn.transiting'),
        direction: dir,
        planet: planetText(t, planet.key),
        sign: getSignAbbr(t, Number(planet.sign)),
        degree: deg !== undefined ? `${Number(deg).toFixed(2)}°` : '',
        color: '#f57c00',
      };
    });

    return [...natal, ...transits];
  });

  const renderQuadrantPlanets = (dir: string) => {
    const natalPlanets = getNatalPlanets(dir);
    const tPlanets = getTransitPlanets(dir);

    if (natalPlanets.length === 0 && tPlanets.length === 0) {
      return (
        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
          {t('bnn.empty')}
        </Typography>
      );
    }

    return (
      <Stack spacing={0.35} alignItems="center">
        {natalPlanets.map((p, idx) => (
          <Typography
            key={`${dir}-${p.key}-${idx}`}
            sx={{
              color: p.isVirtual || p.retroInPrev ? '#7c4dff' : DIR_CONFIG[dir].color,
              fontWeight: 600,
              fontSize: { xs: 14, sm: 18 },
              lineHeight: 1.08,
              textShadow: '0 1px 0 rgba(255,255,255,0.65)',
            }}
          >
            {getPlanetAbbr(t, p.key || p.name)}{p.isRetro ? ' (R) ' : ''}-{Math.round(p.degInSign)}
          </Typography>
        ))}
        {tPlanets.map((p) => {
          const deg = transitSignMap[`${p.key}_DEG`];
          return (
            <Typography
              key={`${dir}-tr-${p.key}`}
              sx={{ color: '#e65100', fontWeight: 600, fontSize: { xs: 13, sm: 17 }, lineHeight: 1.05 }}
            >
              {t('bnn.transitPrefix')}-{getPlanetAbbr(t, p.key)}{deg !== undefined ? `-${Math.round(Number(deg))}` : ''}
            </Typography>
          );
        })}
      </Stack>
    );
  };

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <Paper
        elevation={2}
        sx={{
          overflow: 'hidden',
          borderRadius: 2,
          bgcolor: '#f7fbf7',
          border: '1px solid #b7dcc2',
          scrollMarginTop: 120,
        }}
      >
        <Grid container spacing={2} sx={{ p: { xs: 1.5, md: 2 }, alignItems: 'stretch' }}>
          <Grid size={{ xs: 12, md: referenceChartData ? 6 : 12 }}>
            <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, textAlign: 'center' }}>
              {title}
            </Typography>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: 620,
                aspectRatio: '1 / 1',
                mx: 'auto',
                bgcolor: '#f7fbf7',
                mt: 1,
              }}
            >
              <Box sx={{ position: 'absolute', top: '6%', left: '50%', transform: 'translateX(-50%)', fontSize: { xs: 24, sm: 34 }, color: '#17251b', fontWeight: 600 }}>
                North
              </Box>
              <Box sx={{ position: 'absolute', bottom: '5%', left: '50%', transform: 'translateX(-50%)', fontSize: { xs: 24, sm: 34 }, color: '#17251b', fontWeight: 600 }}>
                South
              </Box>
              <Box sx={{ position: 'absolute', top: '49%', left: { xs: 2, sm: 12 }, transform: 'translateY(-50%)', fontSize: { xs: 22, sm: 32 }, color: '#17251b', fontWeight: 600 }}>
                West
              </Box>
              <Box sx={{ position: 'absolute', top: '49%', right: { xs: 2, sm: 12 }, transform: 'translateY(-50%)', fontSize: { xs: 22, sm: 32 }, color: '#17251b', fontWeight: 600 }}>
                East
              </Box>
              <Box sx={{ position: 'absolute', top: '18%', bottom: '13%', left: '50%', borderLeft: '3px solid #17251b' }} />
              <Box sx={{ position: 'absolute', top: '49%', left: '12%', right: '12%', borderTop: '3px solid #17251b' }} />

              {chartDirections.map((dir) => {
                const pos = quadrantMap[dir];
                const cfg = DIR_CONFIG[dir];
                return (
                  <Box
                    key={dir}
                    sx={{
                      position: 'absolute',
                      top: pos.top,
                      left: pos.left,
                      width: '22%',
                      textAlign: pos.align,
                    }}
                  >
                    <Typography sx={{ color: 'rgba(46,125,50,0.22)', fontWeight: 600, fontSize: { xs: 19, sm: 30 }, lineHeight: 0.95 }}>
                      {directionText(t, dir)}
                    </Typography>
                    <Typography sx={{ color: 'rgba(46,125,50,0.22)', fontWeight: 600, fontSize: { xs: 19, sm: 30 }, lineHeight: 0.95, mb: 1 }}>
                      {pos.label}
                    </Typography>
                    <Typography sx={{ color: cfg.color, fontWeight: 600, fontSize: { xs: 12, sm: 14 }, mb: 0.5 }}>
                      {bnn.directionalGroups[dir]?.signs?.join('-')}
                    </Typography>
                    {renderQuadrantPlanets(dir)}
                  </Box>
                );
              })}
            </Box>
            <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, textAlign: 'center', pb: referenceChartData ? 0 : 1 }}>
              Planetary Positions Nadi
            </Typography>
          </Grid>

          {referenceChartData ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#ffffff',
                  border: '1px solid #d6eadc',
                  borderRadius: 1.5,
                  p: { xs: 1.5, sm: 2 },
                  '& svg': { maxWidth: '100%', height: 'auto' },
                }}
              >
                <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, textAlign: 'center', mb: 1 }}>
                  D1 / Lagna Chart
                </Typography>
                <NorthIndianChart chartData={referenceChartData} size={460} />
              </Box>
            </Grid>
          ) : null}
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ border: '1px solid #b7dcc2' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: '#fff', fontWeight: 600 }}>{t('astrology.planet', { defaultValue: 'Planet' })}</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 600 }}>{t('astrology.sign', { defaultValue: 'Sign' })}</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 600 }}>{t('bnn.direction', { defaultValue: 'Direction' })}</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 600 }}>{t('astrology.degree', { defaultValue: 'Degree' })}</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Type</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {positionRows.map((row) => (
              <TableRow key={row.id} sx={{ bgcolor: '#f7fbf7' }}>
                <TableCell sx={{ color: row.color, fontWeight: 600 }}>{row.planet}</TableCell>
                <TableCell>{row.sign}</TableCell>
                <TableCell sx={{ color: DIR_CONFIG[row.direction]?.color, fontWeight: 600 }}>{directionText(t, row.direction)}</TableCell>
                <TableCell>{row.degree}</TableCell>
                <TableCell>{row.type}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

// ─── Progression Dashboard ────────────────────────────────────────────────────

/** North Indian SVG chart for relational Kundli */
const SpecializedKundli: React.FC<{
  title: string;
  focalPlanet: string;
  focalSign: number;
  houses: RelationalHouse[];
  progressions?: PlanetProgressions;
  color: string;
}> = ({ title, focalPlanet, focalSign, houses, progressions, color }) => {
  const SIZE = 380;
  const { t } = useTranslation();
  const { chartStyle } = useUserPreferences();

  const houseMap: Record<number, RelationalHouse> = {};
  for (const h of houses) houseMap[h.house] = h;
  const chartHouses: NorthIndianHouseLabel[] = Array.from({ length: 12 }, (_, index) => {
    const id = index + 1;
    const house = houseMap[id];
    const sign = house?.sign ?? 0;
    const isFocal = house?.planets.some((planet) => planet.key === focalPlanet.toUpperCase()) ?? false;

    return {
      id,
      houseLabel: '',
      signNumber: sign,
      signLabel: sign ? getSignAbbr(t, sign) : '',
      planets: (house?.planets ?? []).map((planet) => {
        const label = `${getPlanetAbbr(t, planet.key || planet.name)}${planet.isRetro ? 'R' : ''}`;
        return {
          abbr: label,
          color: planet.key === focalPlanet.toUpperCase() ? color : '#1565c0',
          degree: null,
          status: '',
        };
      }),
      lagnaMarker: isFocal ? '●' : undefined,
    };
  });
  const progressionItems = Object.entries(progressions || {}).flatMap(([planet, data]) => (
    (['major', 'minor'] as const)
      .map((type) => data?.[type] ? ({ ...data[type], planet, type }) : null)
      .filter(Boolean) as ProgressionPoint[]
  ));
  const ascSign = houses.find((house) => house.house === 1)?.sign || focalSign || 1;
  const signFixedHouses: NorthIndianHouseLabel[] = Array.from({ length: 12 }, (_, index) => {
    const sign = index + 1;
    const house = houses.find((item) => item.sign === sign);
    const isFocal = house?.planets.some((planet) => planet.key === focalPlanet.toUpperCase()) ?? false;
    const progressionPlanets = progressionItems
      .filter((item) => item.sign === sign)
      .map((item) => {
        const label = PROGRESSION_LABELS[item.planet] || item.planet.slice(0, 2);
        return {
          abbr: `${item.type === 'major' ? '●' : '○'}${label}`,
          color: PROGRESSION_COLORS[item.planet] || '#2e7d52',
          degree: null,
          status: '',
        };
      });
    return {
      id: sign,
      houseLabel: '',
      signNumber: sign,
      signLabel: getSignAbbr(t, sign),
      planets: [
        ...progressionPlanets,
        ...(house?.planets ?? []).map((planet) => ({
          abbr: `${getPlanetAbbr(t, planet.key || planet.name)}${planet.isRetro ? 'R' : ''}`,
          color: planet.key === focalPlanet.toUpperCase() ? color : '#1565c0',
          degree: null,
          status: '',
        })),
      ],
      lagnaMarker: isFocal ? '●' : undefined,
    };
  });

  return (
    <Paper elevation={2} sx={{ p: 2, borderRadius: 2, border: `2px solid ${color}40`, borderTop: `4px solid ${color}` }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color }}>{title}</Typography>
        <Chip
          label={`${t('bnn.pivot')}: ${planetText(t, focalPlanet)} (${t('astrology.sign')} ${focalSign})`}
          size="small"
          sx={{ bgcolor: `${color}18`, color, fontWeight: 600, fontSize: '0.65rem' }}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ position: 'relative', width: SIZE, height: SIZE }}>
          {chartStyle === 'south' ? (
            <SouthIndianChartRenderer houses={signFixedHouses} size={SIZE} ascSign={ascSign} />
          ) : chartStyle === 'east' ? (
            <EastIndianChartRenderer houses={signFixedHouses} size={SIZE} ascSign={ascSign} />
          ) : (
            <>
              <NorthIndianChartRenderer
                houses={chartHouses}
                size={SIZE}
                borderColor="#ccc"
                gridColor="#ccc"
                houseColor="#999"
                signColor="#555"
                showBrand={false}
                style={{ display: 'block' }}
              />
              <ProgressionOverlay progressions={progressionItems} houses={houses} />
            </>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

const ProgressionOverlay: React.FC<{
  progressions: ProgressionPoint[];
  houses: RelationalHouse[];
}> = ({ progressions, houses }) => {
  if (!progressions.length) return null;
  const clamp = (value: number, min = 22, max = 378) => Math.min(max, Math.max(min, value));

  return (
    <svg
      viewBox="0 0 400 400"
      preserveAspectRatio="none"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 2,
      }}
    >
      {progressions.map((item, index) => {
        const house = houses.find((h) => h.sign === item.sign);
        if (!house) return null;
        const base = NORTH_INDIAN_HOUSE_POSITIONS.find((position) => position.id === house.house);
        if (!base) return null;

        const sameHouseIndex = progressions.slice(0, index).filter((previous) => previous.sign === item.sign).length;
        const x = clamp(((base.x + (item.type === 'major' ? -5 : 5)) / 100) * 400);
        const y = clamp(((base.y + 4 + (sameHouseIndex * 8)) / 100) * 400);
        const color = PROGRESSION_COLORS[item.planet] || '#2e7d52';
        const label = PROGRESSION_LABELS[item.planet] || item.planet.slice(0, 2);

        return (
          <g key={`${item.planet}-${item.type}`}>
            <circle
              cx={x}
              cy={y}
              r={item.type === 'major' ? 11 : 8}
              fill={item.type === 'major' ? color : '#ffffff'}
              stroke={color}
              strokeWidth="2"
              strokeDasharray={item.type === 'minor' ? '3 2' : undefined}
            />
            <text
              x={x}
              y={y + 1}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="8"
              fontWeight="800"
              fill={item.type === 'major' ? '#ffffff' : color}
            >
              {label}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

const ProgressionLegend: React.FC = () => (
  <Paper variant="outlined" sx={{ p: 1.5, mt: 2, borderRadius: 2 }}>
    <Grid container spacing={1}>
      {Object.entries(PROGRESSION_COLORS).map(([planet, color]) => (
        <Grid key={planet} size={{ xs: 6, sm: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color }} />
            <Typography variant="caption">{planet}</Typography>
          </Box>
        </Grid>
      ))}
      <Grid size={{ xs: 6 }}>
        <Typography variant="caption">● Major progression</Typography>
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Typography variant="caption">○ Minor progression</Typography>
      </Grid>
    </Grid>
  </Paper>
);

const ProgressionOverview: React.FC<{ progressions?: PlanetProgressions }> = ({ progressions }) => {
  if (!progressions) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Progression Overview
      </Typography>
      <Grid container spacing={1.5}>
        {Object.entries(progressions).map(([planet, data]) => (
          <Grid key={planet} size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: '100%' }}>
              <Typography variant="subtitle2" sx={{ color: PROGRESSION_COLORS[planet] || 'primary.main', fontWeight: 600 }}>
                {planet}
              </Typography>
              {data.major && (
                <Typography variant="caption" display="block" color="text.secondary">
                  Major: {data.major.signName || SIGN_NAMES[data.major.sign]} · {data.major.degree}°
                </Typography>
              )}
              {data.minor && (
                <Typography variant="caption" display="block" color="text.secondary">
                  Minor: {data.minor.signName || SIGN_NAMES[data.minor.sign]} · {data.minor.degree}°
                </Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const formatProgressionDate = (value?: string) => {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const ProgressionPeriodTables: React.FC<{
  tables?: ProgressionTables;
  progressions?: PlanetProgressions;
  targetDate?: string;
}> = ({ tables, progressions, targetDate }) => {
  const { t } = useTranslation();
  const majorRows = (tables?.major || []).slice(0, 48);
  const currentTarget = String(targetDate || '').slice(0, 10);
  const activeMajorKeys = new Set(
    Object.values(progressions || {}).map((data) => `${data?.major?.planet}:${data?.major?.sign}`)
  );
  const minorRows = (tables?.minor || [])
    .filter((row) => (
      activeMajorKeys.has(`${row.planet}:${row.majorSign}`)
      && (!currentTarget || Boolean(row.majorFrom && row.majorFrom <= currentTarget && currentTarget < (row.majorTo || '9999-12-31')))
    ))
    .slice(0, 60);

  if (!majorRows.length && !minorRows.length) return null;

  const renderTable = (title: string, rows: ProgressionTableRow[], isMinor = false) => rows.length ? (
    <Box sx={{ mt: 2.5 }}>
      <Typography variant="subtitle2" sx={{ color: '#f9a825', fontWeight: 600, mb: 1 }}>
        {title}
      </Typography>
      <TableContainer component={Paper} sx={{ borderRadius: 2, bgcolor: '#10131f', border: '1px solid #333' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#555' }}>
              <TableCell sx={{ color: '#fff', fontWeight: 600 }}>{t('astrology.planet', { defaultValue: 'Planet' })}</TableCell>
              {isMinor && <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Major</TableCell>}
              <TableCell sx={{ color: '#fff', fontWeight: 600 }}>{t('astrology.sign', { defaultValue: 'Sign' })}</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 600 }}>From</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 600 }}>To</TableCell>
              {!isMinor && <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Years</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={`${row.planet}-${row.from}-${row.sign}-${index}`} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f3f4f611' } }}>
                <TableCell sx={{ color: '#f9a825', fontWeight: 600 }}>{planetText(t, row.planet)}</TableCell>
                {isMinor && <TableCell sx={{ color: '#ddd' }}>{row.majorSignName || SIGN_NAMES[row.majorSign || 0]}</TableCell>}
                <TableCell sx={{ color: '#fff' }}>
                  {row.signName || SIGN_NAMES[row.sign]} ({row.sign}) {row.isBalancePeriod ? '*' : ''}
                </TableCell>
                <TableCell sx={{ color: '#ddd' }}>{formatProgressionDate(row.from)}</TableCell>
                <TableCell sx={{ color: '#ddd' }}>{formatProgressionDate(row.to)}</TableCell>
                {!isMinor && <TableCell sx={{ color: '#ddd' }}>{row.durationYears ?? '--'}</TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  ) : null;

  return (
    <Box sx={{ mt: 3 }}>
      {renderTable('Major Progression Table', majorRows)}
      {renderTable('Minor Progression Table - Current Major Periods', minorRows, true)}
      {majorRows.some((row) => row.isBalancePeriod) && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
          * Rahu/Ketu balance uses displayed degree / 1.5; after that each previous sign runs for 18 years.
        </Typography>
      )}
    </Box>
  );
};

const BNNProgressionDashboard: React.FC<{
  chartId: string | null;
  bnn: BNNResult;
  chartData: any;
}> = ({ chartId, bnn, chartData }) => {
  const { t } = useTranslation();
  const [targetDate, setTargetDate] = React.useState<string>(
    toLocalYmd(new Date())
  );
  const [progression, setProgression] = React.useState<ProgressionResult | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const fetchProgression = React.useCallback(async (date: string) => {
    setLoading(true);
    setError(null);
    try {
      let body: any;
      if (chartId) {
        body = { chartId, targetDate: date };
      } else {
        // Build from in-memory bnn data
        const natalPlanets = bnn.planets.filter((p) => !p.isVirtual).map((p) => ({
          key:       p.key,
          name:      p.name,
          sign:      p.sign,
          degInSign: p.degInSign,
          isRetro:   p.isRetro,
        }));
        const dob = chartData?.birthDetails?.dateOfBirth || chartData?.dob;
        body = { natalPlanets, dob, targetDate: date };
      }
      const res = await axios.post(`${API_BASE_URL}/bnn/progressions`, body);
      setProgression(res.data.progression);
    } catch (e: any) {
      setError(e.response?.data?.error || t('bnn.progressionFailed'));
    } finally {
      setLoading(false);
    }
  }, [chartId, bnn, chartData, t]);

  React.useEffect(() => { fetchProgression(targetDate); }, []);  // eslint-disable-line

  const shiftMonth = (delta: number) => {
    const d = parseYmdLocal(targetDate);
    d.setMonth(d.getMonth() + delta);
    const next = toLocalYmd(d);
    setTargetDate(next);
    fetchProgression(next);
  };

  const majorCfg = progression ? DIR_CONFIG[progression.major.direction] : undefined;
  const minorCfg = progression ? DIR_CONFIG[progression.minor.direction] : undefined;

  return (
    <Box sx={{ mt: 2 }}>
      {/* ── Header + Date Nav ── */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
          mb: 3,
          p: 2,
          bgcolor: '#0d0d1a',
          borderRadius: 2,
          border: '1px solid #333',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          <ProgressionIcon sx={{ color: '#f9a825' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
            {t('bnn.progressionEngine')}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: '#1a1a2e',
            border: '1px solid #444',
            borderRadius: 2,
            px: 1,
            py: 0.5,
          }}
        >
          <IconButton size="small" onClick={() => shiftMonth(-1)} sx={{ color: 'white' }}>
            <NavigateBefore />
          </IconButton>
          <CalendarIcon sx={{ color: '#f9a825', fontSize: 18 }} />
          <TextField
            type="date"
            size="small"
            value={targetDate}
            onChange={(e) => {
              setTargetDate(e.target.value);
              if (e.target.value) fetchProgression(e.target.value);
            }}
            inputProps={{ style: { color: 'white', fontFamily: 'monospace', fontSize: '0.85rem' } }}
            sx={{
              '& fieldset': { border: 'none' },
              '& input': { color: 'white' },
              width: 148,
            }}
          />
          <IconButton size="small" onClick={() => shiftMonth(1)} sx={{ color: 'white' }}>
            <NavigateNext />
          </IconButton>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && progression && (
        <>
          {/* ── Major / Minor stat cards ── */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Paper
                elevation={2}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  bgcolor: '#1a0e00',
                  border: `2px solid ${majorCfg?.color || '#f9a825'}40`,
                  borderTop: `4px solid ${majorCfg?.color || '#f9a825'}`,
                }}
              >
                <Typography variant="caption" sx={{ color: '#f9a825', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                  {t('bnn.majorProgressionYearly')}
                </Typography>
                <Typography variant="h5" sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'white', mt: 0.5 }}>
                  {planetText(t, 'Jupiter')} {t('bnn.inSign')} {t(`astrology.signs.${progression.major.sign}`, { defaultValue: SIGN_NAMES[progression.major.sign] })}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={`${t('astrology.sign')} ${progression.major.sign}`}
                    size="small"
                    sx={{ bgcolor: `${majorCfg?.color}22`, color: majorCfg?.color, fontWeight: 600 }}
                  />
                  <Chip
                    label={directionText(t, progression.major.direction)}
                    size="small"
                    sx={{ bgcolor: `${majorCfg?.color}22`, color: majorCfg?.color }}
                  />
                  <Chip
                    label={`${t('bnn.cycleYear')} ${progression.major.year}`}
                    size="small"
                    sx={{ bgcolor: '#333', color: '#aaa' }}
                  />
                </Box>
                {progression.major.activated.length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" sx={{ color: '#aaa' }}>{t('bnn.natalPlanetsHere')}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                      {progression.major.activated.map((p) => (
                        <Chip
                          key={p.key}
                          label={planetText(t, p.name)}
                          size="small"
                          sx={{ bgcolor: '#f9a825', color: '#000', fontWeight: 600, fontSize: '0.65rem' }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Paper
                elevation={2}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  bgcolor: '#001530',
                  border: `2px solid ${minorCfg?.color || '#64b5f6'}40`,
                  borderTop: `4px solid ${minorCfg?.color || '#64b5f6'}`,
                }}
              >
                <Typography variant="caption" sx={{ color: '#64b5f6', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                  {t('bnn.minorProgressionMonthly')}
                </Typography>
                <Typography variant="h5" sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'white', mt: 0.5 }}>
                  {planetText(t, 'Jupiter')} {t('bnn.inSign')} {t(`astrology.signs.${progression.minor.sign}`, { defaultValue: SIGN_NAMES[progression.minor.sign] })}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={`${t('astrology.sign')} ${progression.minor.sign}`}
                    size="small"
                    sx={{ bgcolor: `${minorCfg?.color}22`, color: minorCfg?.color, fontWeight: 600 }}
                  />
                  <Chip
                    label={directionText(t, progression.minor.direction)}
                    size="small"
                    sx={{ bgcolor: `${minorCfg?.color}22`, color: minorCfg?.color }}
                  />
                  <Chip
                    label={`${t('bnn.month')} ${(progression.minor.month ?? 0) + 1} ${t('bnn.of12')}`}
                    size="small"
                    sx={{ bgcolor: '#333', color: '#aaa' }}
                  />
                </Box>
                {progression.minor.activated.length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" sx={{ color: '#aaa' }}>{t('bnn.natalPlanetsHere')}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                      {progression.minor.activated.map((p) => (
                        <Chip
                          key={p.key}
                          label={planetText(t, p.name)}
                          size="small"
                          sx={{ bgcolor: '#64b5f6', color: '#000', fontWeight: 600, fontSize: '0.65rem' }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>
            {progression.progressions?.Saturn?.major && progression.progressions?.Saturn?.minor && (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2, bgcolor: '#161616', border: '2px solid #64748B40', borderTop: '4px solid #64748B' }}>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                      Saturn Major Progression
                    </Typography>
                    <Typography variant="h5" sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'white', mt: 0.5 }}>
                      {planetText(t, 'Saturn')} {t('bnn.inSign')} {t(`astrology.signs.${progression.progressions.Saturn.major.sign}`, { defaultValue: SIGN_NAMES[progression.progressions.Saturn.major.sign] })}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                      <Chip label={`${t('astrology.sign')} ${progression.progressions.Saturn.major.sign}`} size="small" sx={{ bgcolor: '#64748B22', color: '#94a3b8', fontWeight: 600 }} />
                      <Chip label={directionText(t, progression.progressions.Saturn.major.direction)} size="small" sx={{ bgcolor: '#64748B22', color: '#94a3b8' }} />
                    </Box>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2, bgcolor: '#101827', border: '2px solid #38bdf840', borderTop: '4px solid #38bdf8' }}>
                    <Typography variant="caption" sx={{ color: '#38bdf8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                      Saturn Minor Progression
                    </Typography>
                    <Typography variant="h5" sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'white', mt: 0.5 }}>
                      {planetText(t, 'Saturn')} {t('bnn.inSign')} {t(`astrology.signs.${progression.progressions.Saturn.minor.sign}`, { defaultValue: SIGN_NAMES[progression.progressions.Saturn.minor.sign] })}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                      <Chip label={`${t('astrology.sign')} ${progression.progressions.Saturn.minor.sign}`} size="small" sx={{ bgcolor: '#38bdf822', color: '#38bdf8', fontWeight: 600 }} />
                      <Chip label={directionText(t, progression.progressions.Saturn.minor.direction)} size="small" sx={{ bgcolor: '#38bdf822', color: '#38bdf8' }} />
                      <Chip label={`${t('bnn.month')} ${(progression.progressions.Saturn.minor.month ?? 0) + 1} ${t('bnn.of12')}`} size="small" sx={{ bgcolor: '#333', color: '#aaa' }} />
                    </Box>
                  </Paper>
                </Grid>
              </>
            )}
          </Grid>

          {/* ── Hinglish interpretation ── */}
          <Paper
            sx={{
              p: 2,
              mb: 3,
              bgcolor: '#0d1117',
              border: '1px solid #f9a82540',
              borderRadius: 2,
            }}
          >
            <Typography variant="caption" sx={{ color: '#f9a825', fontWeight: 600, textTransform: 'uppercase' }}>
              {t('bnn.interpretationHinglish')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: '#ddd', fontStyle: 'italic', lineHeight: 1.8 }}>
              {translateFreeText(t, progression.hinglish)}
            </Typography>
          </Paper>

          {/* ── Jeeva & Karma Kundli ── */}
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            {t('bnn.jeevaKarmaKundli')}
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 6 }}>
              <SpecializedKundli
                title={t('bnn.jeevaKundliSelf')}
                focalPlanet="Jupiter"
                focalSign={progression.jeevaSign}
                houses={progression.jeevaHouses}
                progressions={progression.progressions}
                color="#f9a825"
              />
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <SpecializedKundli
                title={t('bnn.karmaKundliAction')}
                focalPlanet="Saturn"
                focalSign={progression.karmaSign}
                houses={progression.karmaHouses}
                progressions={progression.progressions}
                color="#7c4dff"
              />
            </Grid>
          </Grid>
          <ProgressionLegend />
          <ProgressionOverview progressions={progression.progressions} />
          <ProgressionPeriodTables tables={progression.progressionTables} progressions={progression.progressions} targetDate={progression.targetDate} />
        </>
      )}
    </Box>
  );
};

// ─── Main Transit Section wrapper ───────────────────────────────────────────

const TransitSection: React.FC<{
  transit: TransitResult;
  selfKaraka: { key: string; name: string };
  bnn: BNNResult;
  seoPath: string;
}> = ({ transit, selfKaraka, bnn, seoPath }) => {
  const { t, i18n } = useTranslation();
  const { currentTriggers, timeline, transitSignMap, transitDate } = transit;
  const dateStr = new Date(transitDate).toLocaleDateString(i18n.language === 'hi' ? 'hi-IN' : 'en-IN', { day:'numeric', month:'long', year:'numeric' });
  const [tab, setTab] = React.useState(0);

  return (
    <Box sx={{ mt: 2 }}>
      <SeoHead
        title={`${BNN_TRANSIT_TAB_META[tab]?.title || 'BNN Analysis'} | Kundlistar`}
        description={BNN_TRANSIT_TAB_META[tab]?.description || 'BNN transit analysis for this chart.'}
        path={seoPath}
        robots="noindex,nofollow"
        keywords="BNN analysis, Bhrigu Nandi Nadi, transit activations, directional chart, Kundlistar"
      />

      {/* Header */}
      <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:2 }}>
        <TimelineIcon sx={{ color:'#f9a825' }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('bnn.transitAnalysis')}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml:'auto' }}>
          {t('bnn.asOf')} {dateStr}
        </Typography>
      </Box>

      {/* Current transit sign strip */}
      <Box sx={{ display:'flex', flexWrap:'wrap', gap:1, mb:2 }}>
        {Object.entries(transitSignMap)
          .filter(([k]) => !k.includes('_DEG'))
          .map(([key, sign]) => {
            const dir = signDir(sign as number);
            const cfg = DIR_CONFIG[dir];
            const deg = transitSignMap[`${key}_DEG`];
            return (
              <Chip
                key={key}
                label={`${planetText(t, key)} • ${getSignAbbr(t, sign as number)}${deg !== undefined ? ' '+Number(deg).toFixed(1)+'°' : ''}`}
                size="small"
                sx={{ bgcolor:`${cfg.color}18`, border:`1px solid ${cfg.color}`, color:cfg.color, fontWeight:600, fontSize:'0.7rem' }}
              />
            );
          })}
      </Box>

      {/* Sub-tabs */}
      <Box sx={{ borderBottom:1, borderColor:'divider', mb:2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} textColor="primary" indicatorColor="primary"
          sx={{ '& .MuiTab-root': { textTransform:'none', fontWeight:500, minWidth:120 } }}
        >
          <Tab label={t('bnn.timeline')} />
          <Tab label={t('bnn.activations')} />
          <Tab label={t('bnn.directionalChart')} />
        </Tabs>
      </Box>

      {tab === 0 && (
        timeline.length === 0
          ? <Paper sx={{ p:2, bgcolor:'#f5f5f5', borderRadius:2 }}>
              <Typography variant="body2" color="text.secondary">{t('bnn.noProjectedActivations')}</Typography>
            </Paper>
          : <TimelineTable timeline={timeline} />
      )}

      {tab === 1 && (
        currentTriggers.length === 0
          ? <Paper sx={{ p:2, bgcolor:'#f5f5f5', borderRadius:2 }}>
              <Typography variant="body2" color="text.secondary">{t('bnn.noActiveTriggers')}</Typography>
            </Paper>
          : <ActivationsTable triggers={currentTriggers} selfKaraka={selfKaraka} transitSignMap={transitSignMap} />
      )}

      {tab === 2 && (
        <DirectionalTransitChart bnn={bnn} transitSignMap={transitSignMap} />
      )}
    </Box>
  );
};
// ─── Planet-level badge helpers ──────────────────────────────────────────────

const JIVA_KEY  = 'JUPITER';
const KARMA_KEY = 'SATURN';

/** Build a single Hinglish tooltip string for all combos this planet has in the given direction. */
function buildComboTooltip(planet: BNNPlanet, direction: string, nadiStrings: NadiString[]): string {
  const relevant = nadiStrings.filter(
    (ns) => ns.direction === direction && ns.keys.includes(planet.key) && !ns.keys.every((k) => k === planet.key)
  );
  return relevant.map((ns) => ns.interpretation.hi).join(' | ');
}

// ─── PlanetCard ──────────────────────────────────────────────────────────────

const PlanetCard: React.FC<{
  planet: BNNPlanet;
  dirColor: string;
  isSource: boolean;
  comboTooltip: string;
}> = ({ planet, dirColor, isSource, comboTooltip }) => {
  const { t } = useTranslation();
  const isJiva  = planet.key === JIVA_KEY;
  const isKarma = planet.key === KARMA_KEY;
  const isVirt  = planet.isVirtual || planet.retroInPrev;

  const borderColor = isJiva ? '#f9a825' : isKarma ? '#3949ab' : dirColor;
  const bgColor     = isVirt ? 'transparent'
    : isJiva  ? '#fff8e1'
    : isKarma ? '#e8eaf6'
    : `${dirColor}14`;

  const tooltipContent = (
    <Box>
      <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>
        {planetText(t, planet.name)} • {t(`astrology.signs.${planet.sign}`, { defaultValue: SIGN_NAMES[planet.sign] })} {planet.degInSign.toFixed(2)}°
        {planet.isRetro ? ' ℞' : ''}
        {isVirt ? ` (${planet.note ? translateFreeText(t, planet.note) : t('bnn.virtual')})` : ''}
      </Typography>
      {comboTooltip && (
        <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic', maxWidth: 260 }}>
          {comboTooltip}
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} arrow placement="right">
      <Box
        sx={{
          p: '6px 10px',
          borderRadius: 1.5,
          bgcolor: bgColor,
          border: `1.5px ${isVirt ? 'dashed' : 'solid'} ${borderColor}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'default',
          opacity: isVirt ? 0.72 : 1,
          transition: 'box-shadow 0.15s',
          '&:hover': { boxShadow: `0 0 0 2px ${borderColor}44` },
        }}
      >
        {/* Left: name + badges */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: borderColor, lineHeight: 1 }}
          >
            {planetText(t, planet.name)}{planet.isRetro ? ' ℞' : ''}
          </Typography>
          {isSource && (
            <Chip
              label={t('bnn.source')}
              size="small"
              sx={{ height: 15, fontSize: '0.55rem', px: 0.25, bgcolor: dirColor, color: '#fff', fontWeight: 600 }}
            />
          )}
          {isJiva && (
            <Chip
              label={t('bnn.jiva')}
              size="small"
              sx={{ height: 15, fontSize: '0.55rem', px: 0.25, bgcolor: '#f9a825', color: '#000', fontWeight: 600 }}
            />
          )}
          {isKarma && (
            <Chip
              label={t('bnn.karma')}
              size="small"
              sx={{ height: 15, fontSize: '0.55rem', px: 0.25, bgcolor: '#3949ab', color: '#fff', fontWeight: 600 }}
            />
          )}
          {isVirt && (
            <Chip
              label={planet.note === 'Parivartana Exchange' ? t('bnn.exchange') : t('bnn.shadow')}
              size="small"
              variant="outlined"
              sx={{ height: 15, fontSize: '0.55rem', px: 0.25 }}
            />
          )}
        </Box>
        {/* Right: degree */}
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: borderColor, fontFamily: 'monospace', ml: 1, whiteSpace: 'nowrap' }}
        >
          {planet.degInSign.toFixed(1)}°
        </Typography>
      </Box>
    </Tooltip>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const DirectionCard: React.FC<{
  direction: string;
  data: DirectionData;
  nadiStrings: NadiString[];
}> = ({ direction, data, nadiStrings }) => {
  const { t } = useTranslation();
  const cfg = DIR_CONFIG[direction];
  const { Icon } = cfg;
  // Index of first real planet = Source
  const firstRealIdx = data.planets.findIndex((p) => !p.isVirtual && !p.retroInPrev);

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        border: `2px solid ${cfg.color}40`,
        borderTop: `4px solid ${cfg.color}`,
        borderRadius: 2,
        bgcolor: cfg.bg,
        height: '100%',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Icon sx={{ color: cfg.color, fontSize: 20 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: cfg.color, flex: 1 }}>
          {directionText(t, direction)} ({data.signs.join('-')})
        </Typography>
        <Chip
          label={elementText(t, data.element)}
          size="small"
          sx={{ bgcolor: `${cfg.color}20`, color: cfg.color, fontWeight: 600, fontSize: '0.6rem' }}
        />
      </Box>

      {/* Planet list with flow arrows */}
      {data.planets.length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {t('bnn.noPlanets')}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {data.planets.map((p, idx) => (
            <React.Fragment key={`${p.key}-${idx}`}>
              <PlanetCard
                planet={p}
                dirColor={cfg.color}
                isSource={idx === firstRealIdx}
                comboTooltip={buildComboTooltip(p, direction, nadiStrings)}
              />
              {idx < data.planets.length - 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.25 }}>
                  <ArrowIcon
                    sx={{
                      fontSize: 14,
                      color: `${cfg.color}80`,
                      transform: 'rotate(90deg)',
                    }}
                  />
                </Box>
              )}
            </React.Fragment>
          ))}
        </Box>
      )}
    </Paper>
  );
};

const localGuidanceText = (value: BNNLocalizedText | undefined, isHindi: boolean) => (
  isHindi ? value?.hi : value?.en
) || value?.en || value?.hi || '';

const formatDegreeMinutes = (degree: number | undefined) => {
  const value = Number(degree);
  if (!Number.isFinite(value)) return '--';
  const wholeDegrees = Math.floor(value);
  const minutes = Math.round((value - wholeDegrees) * 60);
  if (minutes === 60) return `${wholeDegrees + 1}°00'`;
  return `${wholeDegrees}°${String(minutes).padStart(2, '0')}'`;
};

const FALLBACK_FIELD_MAP: Record<string, string[]> = {
  SUN: ['government', 'administration', 'leadership', 'public authority', 'management'],
  MOON: ['public dealing', 'hospitality', 'food', 'travel', 'care services'],
  MARS: ['engineering', 'technical work', 'machinery', 'construction', 'property', 'defence'],
  MERCURY: ['IT/software', 'data analytics', 'accounting', 'commerce', 'business systems', 'communication'],
  JUPITER: ['teaching', 'law', 'finance', 'banking', 'advisory', 'coaching', 'spiritual guidance'],
  VENUS: ['design', 'arts', 'luxury', 'fashion', 'beauty', 'vehicles', 'finance', 'media'],
  SATURN: ['operations', 'service', 'large organisations', 'process work', 'long-term structured career'],
  RAHU: ['technology', 'AI/internet', 'foreign work', 'MNC', 'unconventional career', 'research'],
  KETU: ['research', 'deep technical work', 'astrology', 'spiritual work', 'independent consulting', 'back-end work'],
};

const FALLBACK_EDU_MAP: Record<string, string[]> = {
  ...FALLBACK_FIELD_MAP,
  MERCURY: ['computer/software', 'commerce', 'accounting', 'analytics', 'business', 'communication'],
  KETU: ['research', 'mathematics', 'astrology/occult studies', 'spiritual studies', 'deep technical subjects'],
  RAHU: ['AI', 'cyber', 'foreign language', 'internet', 'advanced technology'],
  SUN: ['government exams', 'administration', 'political science', 'management'],
};

const FALLBACK_HEALTH_AREAS: Record<string, string[]> = {
  SUN: ['vitality', 'heart/bones caution', 'heat/acidity pattern'],
  MOON: ['stress/sleep pattern', 'emotional balance', 'cold/sinus tendency'],
  MARS: ['blood pressure pattern', 'injury caution', 'inflammation'],
  MERCURY: ['nervous system', 'skin/respiratory pattern', 'overthinking stress'],
  JUPITER: ['digestion/liver/weight pattern', 'breathing/oxygenation caution'],
  VENUS: ['kidney/reproductive/endocrine pattern', 'sugar/luxury imbalance caution'],
  SATURN: ['chronic fatigue', 'long-term stress', 'stiffness/blockage pattern'],
  RAHU: ['allergy-like pattern', 'anxiety/confusion', 'sudden imbalance'],
  KETU: ['unclear symptoms', 'low energy', 'sleep disturbance'],
};

const FALLBACK_PLANET_HI: Record<string, string> = {
  SUN: 'सूर्य', MOON: 'चंद्र', MARS: 'मंगल', MERCURY: 'बुध', JUPITER: 'गुरु', VENUS: 'शुक्र', SATURN: 'शनि', RAHU: 'राहु', KETU: 'केतु',
  Sun: 'सूर्य', Moon: 'चंद्र', Mars: 'मंगल', Mercury: 'बुध', Jupiter: 'गुरु', Venus: 'शुक्र', Saturn: 'शनि', Rahu: 'राहु', Ketu: 'केतु',
};

const FALLBACK_FIELD_HI: Record<string, string> = {
  research: 'अनुसंधान', government: 'सरकारी क्षेत्र', administration: 'प्रशासन', leadership: 'नेतृत्व', 'public authority': 'लोक-प्राधिकरण', management: 'प्रबंधन',
  'IT/software': 'आईटी/सॉफ्टवेयर', 'data analytics': 'डेटा विश्लेषण', accounting: 'लेखा', commerce: 'वाणिज्य', 'business systems': 'बिजनेस सिस्टम', communication: 'संचार',
  operations: 'संचालन', service: 'सेवा क्षेत्र', 'large organisations': 'बड़े संगठन', 'large organizations': 'बड़े संगठन', 'process work': 'प्रक्रिया-आधारित कार्य',
  technology: 'तकनीक', 'AI/internet': 'एआई/इंटरनेट', 'foreign work': 'विदेशी कार्य', MNC: 'बहुराष्ट्रीय कंपनी', 'unconventional career': 'अपरंपरागत करियर',
  'deep technical work': 'गहन तकनीकी कार्य', astrology: 'ज्योतिष', 'spiritual work': 'आध्यात्मिक कार्य', 'independent consulting': 'स्वतंत्र परामर्श', 'back-end work': 'बैकएंड कार्य',
  'government exams': 'सरकारी परीक्षाएं', 'political science': 'राजनीति विज्ञान', 'computer/software': 'कंप्यूटर/सॉफ्टवेयर', business: 'व्यवसाय', AI: 'एआई', cyber: 'साइबर',
  'foreign language': 'विदेशी भाषा', internet: 'इंटरनेट', 'advanced technology': 'उन्नत तकनीक', mathematics: 'गणित', 'astrology/occult studies': 'ज्योतिष/गूढ़ अध्ययन',
  'spiritual studies': 'आध्यात्मिक अध्ययन', 'deep technical subjects': 'गहन तकनीकी विषय', teaching: 'शिक्षण', law: 'कानून', finance: 'वित्त', banking: 'बैंकिंग',
  advisory: 'परामर्श', coaching: 'कोचिंग', 'spiritual guidance': 'आध्यात्मिक मार्गदर्शन', design: 'डिजाइन', arts: 'कला', luxury: 'लक्जरी', fashion: 'फैशन',
  beauty: 'सौंदर्य', vehicles: 'वाहन', media: 'मीडिया', engineering: 'इंजीनियरिंग', 'technical work': 'तकनीकी कार्य', machinery: 'मशीनरी', construction: 'निर्माण',
  property: 'संपत्ति', defence: 'रक्षा', 'public dealing': 'जन-संपर्क', hospitality: 'आतिथ्य', food: 'भोजन/खाद्य क्षेत्र', travel: 'यात्रा', 'care services': 'देखभाल सेवाएं',
  'stress/sleep pattern': 'तनाव/नींद का पैटर्न', 'emotional balance': 'भावनात्मक संतुलन', 'cold/sinus tendency': 'सर्दी/साइनस प्रवृत्ति',
  'blood pressure pattern': 'रक्तचाप पैटर्न', 'injury caution': 'चोट से सावधानी', inflammation: 'सूजन', 'chronic fatigue': 'दीर्घकालिक थकान',
  'long-term stress': 'लंबे समय का तनाव', 'stiffness/blockage pattern': 'जकड़न/अवरोध पैटर्न', 'allergy-like pattern': 'एलर्जी जैसा पैटर्न',
  'anxiety/confusion': 'चिंता/भ्रम', 'sudden imbalance': 'अचानक असंतुलन', 'unclear symptoms': 'अस्पष्ट लक्षण', 'low energy': 'कम ऊर्जा', 'sleep disturbance': 'नींद में बाधा',
};

const fallbackText = (en: string, hi?: string): BNNLocalizedText => ({ en, hi: hi || en });
const fallbackPlanetHi = (value: string) => FALLBACK_PLANET_HI[value] || FALLBACK_PLANET_HI[String(value || '').toUpperCase()] || value;
const fallbackFieldHi = (value: string) => FALLBACK_FIELD_HI[value] || value;
const fallbackPlanetKey = (planet: BNNPlanet | undefined) => String(planet?.key || planet?.name || '').toUpperCase();
const fallbackRel = (fromSign: number, toSign: number) => ((toSign - fromSign + 12) % 12) + 1;
const fallbackConfidence = (score: number) => (score >= 72 ? 'high' : score >= 50 ? 'medium' : 'low');
const fallbackBirthYear = (chart: any) => {
  const value = chart?.birthDetails?.dateOfBirth || chart?.dateOfBirth || chart?.dob;
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.getFullYear();
};

function buildFallbackLinks(planets: BNNPlanet[], karakaKey: string) {
  const karaka = planets.find((p) => fallbackPlanetKey(p) === karakaKey);
  if (!karaka) return [];
  return planets
    .filter((p) => fallbackPlanetKey(p) !== karakaKey)
    .map((p) => {
      const rel = fallbackRel(karaka.sign, p.sign);
      const score = [1, 5, 9].includes(rel) ? 36 : [3, 7, 11].includes(rel) ? 24 : [2, 12].includes(rel) ? 14 : 6;
      return { planet: fallbackPlanetKey(p), planetName: p.name, relativeHouse: rel, score, reason: fallbackText(`${p.name} is ${rel} from ${karaka.name}`, `${fallbackPlanetHi(p.name)} ${fallbackPlanetHi(karaka.name)} से ${rel}वें संबंध में है।`) };
    })
    .filter((link) => link.score >= 14)
    .sort((a, b) => b.score - a.score);
}

function buildFallbackFieldResult(bnn: BNNResult, karakaKey: string, map: Record<string, string[]>, title: BNNLocalizedText, baseReason: BNNLocalizedText): BNNConcreteFieldResult {
  const scores = new Map<string, { score: number; reasons: BNNLocalizedText[] }>();
  const add = (field: string, score: number, reason: BNNLocalizedText) => {
    const prev = scores.get(field) || { score: 0, reasons: [] };
    prev.score += score;
    prev.reasons.push(reason);
    scores.set(field, prev);
  };
  for (const field of map[karakaKey] || []) add(field, 24, baseReason);
  for (const link of buildFallbackLinks(bnn.planets, karakaKey).slice(0, 6)) {
    for (const field of map[link.planet] || []) add(field, link.score, link.reason);
  }
  const suitedFor = Array.from(scores.entries())
    .map(([value, data]) => ({ value, label: fallbackText(value, fallbackFieldHi(value)), score: data.score, reasons: data.reasons.slice(0, 3) }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 8);
  const score = suitedFor[0]?.score || 0;
  return {
    title,
    confidence: fallbackConfidence(score + 35),
    score,
    result: fallbackText(
      suitedFor.length ? `Best suited for ${suitedFor.map((item) => item.value).join(', ')}.` : 'No clear field cluster found.',
      suitedFor.length ? `इन क्षेत्रों के लिए सबसे उपयुक्त: ${suitedFor.map((item) => item.label.hi).join(', ')}।` : 'स्पष्ट क्षेत्र समूह नहीं मिला।'
    ),
    suitedFor,
    reasons: [baseReason, ...buildFallbackLinks(bnn.planets, karakaKey).slice(0, 4).map((link) => link.reason)],
  };
}

function buildFallbackWindows(bnn: BNNResult, karakaKey: string, chart: any): BNNYearWindow[] {
  const birthYear = fallbackBirthYear(chart);
  const karaka = bnn.planets.find((p) => fallbackPlanetKey(p) === karakaKey);
  const links = buildFallbackLinks(bnn.planets, karakaKey);
  const seed = ((karaka?.sign || 1) + (links[0]?.relativeHouse || 1)) % 5;
  return [24 + seed, 28 + ((seed + 2) % 4), 32 + ((seed + 3) % 4), 36 + seed].map((age, index) => ({
    startAge: age,
    endAge: age + 1,
    startYear: birthYear ? birthYear + age : null,
    endYear: birthYear ? birthYear + age + 1 : null,
    score: Math.min(88, 54 + (links[index]?.score || links[0]?.score || 14)),
    confidence: fallbackConfidence(Math.min(88, 54 + (links[index]?.score || links[0]?.score || 14))),
    reasons: [fallbackText(`${karakaKey} selected as topic karaka.`, `${fallbackPlanetHi(karakaKey)} को विषय कारक माना गया है।`), links[index]?.reason || links[0]?.reason || fallbackText('Jupiter cycle activation from karaka sign.', 'कारक राशि से गुरु चक्र सक्रियता।')],
  }));
}

function buildFallbackGuidance(bnn: BNNResult, chart: any): BNNGuidance {
  const marriageWindows = buildFallbackWindows(bnn, 'VENUS', chart);
  const childrenWindows = buildFallbackWindows(bnn, 'JUPITER', chart);
  const foreignWindows = buildFallbackWindows(bnn, 'RAHU', chart);
  const wellnessWindows = buildFallbackWindows(bnn, 'MOON', chart);
  const wellnessPlanets = ['MOON', 'SATURN', 'MARS', 'RAHU', 'KETU'];
  const wellnessAreas = Array.from(new Set(wellnessPlanets.flatMap((key) => FALLBACK_HEALTH_AREAS[key] || []))).slice(0, 6).map((area) => fallbackText(area));
  const concrete = {
    career: buildFallbackFieldResult(bnn, 'SATURN', FALLBACK_FIELD_MAP, fallbackText('Career Result', 'करियर परिणाम'), fallbackText('Saturn is the main BNN karaka for profession.', 'बीएनएन में शनि को पेशे का मुख्य कारक माना गया है।')),
    education: buildFallbackFieldResult(bnn, 'MERCURY', FALLBACK_EDU_MAP, fallbackText('Education Result', 'शिक्षा परिणाम'), fallbackText('Mercury is selected as the BNN karaka for education and skills.', 'बुध को शिक्षा और कौशल का बीएनएन कारक माना गया है।')),
    marriage: {
      title: fallbackText('Marriage Result', 'विवाह परिणाम'),
      result: fallbackText(`High-probability guidance windows: ${marriageWindows.map((w) => w.startYear ? `${w.startYear}-${w.endYear}` : `age ${w.startAge}-${w.endAge}`).join(', ')}.`, `उच्च-संभावना वाले मार्गदर्शन समय: ${marriageWindows.map((w) => w.startYear ? `${w.startYear}-${w.endYear} (उम्र ${w.startAge}-${w.endAge})` : `उम्र ${w.startAge}-${w.endAge}`).join(', ')}।`),
      windows: marriageWindows,
      nature: buildFallbackLinks(bnn.planets, 'VENUS').slice(0, 3).map((link) => link.reason),
      reasons: [fallbackText('Venus selected as marriage karaka.', 'शुक्र को विवाह का कारक माना गया है।')],
    },
    children: {
      title: fallbackText('Children Result', 'संतान परिणाम'),
      result: fallbackText(`Supportive children-related windows: ${childrenWindows.map((w) => w.startYear ? `${w.startYear}-${w.endYear}` : `age ${w.startAge}-${w.endAge}`).join(', ')}.`, `संतान से जुड़े सहायक समय: ${childrenWindows.map((w) => w.startYear ? `${w.startYear}-${w.endYear} (उम्र ${w.startAge}-${w.endAge})` : `उम्र ${w.startAge}-${w.endAge}`).join(', ')}।`),
      windows: childrenWindows,
      reasons: [fallbackText('Jupiter selected as children karaka.', 'गुरु को संतान का कारक माना गया है।')],
    },
    foreign: {
      title: fallbackText('Foreign / Relocation Result', 'विदेश / स्थानांतरण परिणाम'),
      result: fallbackText(`Foreign, relocation or cross-border work can activate around ${foreignWindows.map((w) => w.startYear ? `${w.startYear}-${w.endYear}` : `age ${w.startAge}-${w.endAge}`).join(', ')}.`, `विदेश, स्थानांतरण या सीमा-पार कार्य ${foreignWindows.map((w) => w.startYear ? `${w.startYear}-${w.endYear} (उम्र ${w.startAge}-${w.endAge})` : `उम्र ${w.startAge}-${w.endAge}`).join(', ')} के आसपास सक्रिय हो सकता है।`),
      windows: foreignWindows,
      reasons: [fallbackText('Rahu selected for foreign, unusual and cross-border indications.', 'राहु को विदेश, असामान्य और सीमा-पार संकेतों के लिए देखा गया है।')],
    },
    wellness: {
      title: fallbackText('Wellness Result', 'स्वास्थ्य सावधानी परिणाम'),
      result: fallbackText(`Health caution years: ${wellnessWindows.map((w) => w.startYear || `age ${w.startAge}`).join(', ')}.`, `स्वास्थ्य सावधानी वर्ष: ${wellnessWindows.map((w) => w.startYear || `उम्र ${w.startAge}`).join(', ')}।`),
      cautionYears: wellnessWindows,
      watchAreas: wellnessAreas,
      reasons: [
        fallbackText('Life significators and afflicting links were checked with Jupiter protection logic.', 'जीवन संकेतक और कष्टकारी संबंधों को गुरु सुरक्षा तर्क के साथ देखा गया है।'),
        fallbackText('Use this only for self-care awareness and consult a qualified professional for health concerns.', 'इसे केवल आत्म-देखभाल जागरूकता के लिए उपयोग करें; स्वास्थ्य चिंता हो तो योग्य विशेषज्ञ से सलाह लें।'),
      ],
    },
  };
  return {
    concrete,
    career: { primaryFields: concrete.career.suitedFor.map((item) => ({ field: fallbackText(item.value), score: item.score, confidence: fallbackConfidence(item.score), reasons: item.reasons })), workStyle: [], cautions: [], ruleTrace: [] },
    topics: [],
    eventGuidance: [],
    disclaimer: fallbackText('BNN guidance shows astrological indications for reflection and planning. It is not a guarantee and should not replace professional advice.', 'बीएनएन मार्गदर्शन चिंतन और योजना के लिए ज्योतिषीय संकेत दिखाता है। यह गारंटी नहीं है और पेशेवर सलाह का विकल्प नहीं है।'),
  };
}

const BNNGuidanceSection: React.FC<{
  guidance: BNNGuidance;
  isHindi: boolean;
}> = ({ guidance, isHindi }) => {
  const { t } = useTranslation();
  const confidenceColor = (value: string) => (
    value === 'high' ? 'success' : value === 'medium' ? 'warning' : 'default'
  ) as 'success' | 'warning' | 'default';
  const formatWindow = (window: BNNYearWindow) => (
    window.startYear
      ? `${window.startYear}-${window.endYear} (${t('bnn.age')} ${window.startAge}-${window.endAge})`
      : `${t('bnn.age')} ${window.startAge}-${window.endAge}`
  );
  const renderReasons = (reasons: BNNLocalizedText[] = []) => (
    <Stack spacing={0.5} sx={{ mt: 1 }}>
      {reasons.slice(0, 5).map((reason, index) => (
        <Typography key={`${localGuidanceText(reason, false)}-${index}`} variant="body2" color="text.secondary">
          {localGuidanceText(reason, isHindi)}
        </Typography>
      ))}
    </Stack>
  );

  if (guidance.concrete) {
    const { concrete } = guidance;
    const fieldCards = [concrete.career, concrete.education];
    const timedCards = [concrete.marriage, concrete.children, concrete.foreign];

    return (
      <Stack spacing={3}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('bnn.guidanceTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('bnn.guidanceVerdictSubtitle')}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {fieldCards.map((card) => (
            <Grid size={{ xs: 12, md: 6 }} key={localGuidanceText(card.title, false)}>
              <Paper variant="outlined" sx={{ p: 2.25, height: '100%', borderRadius: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <StarIcon fontSize="small" color="warning" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {localGuidanceText(card.title, isHindi)}
                  </Typography>
                  <Chip size="small" color={confidenceColor(card.confidence)} label={t(`bnn.guidanceConfidence.${card.confidence}`, { defaultValue: card.confidence })} />
                </Stack>
                <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.dark', mb: 1 }}>
                  {localGuidanceText(card.result, isHindi)}
                </Typography>
                <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                  {card.suitedFor.slice(0, 8).map((item) => (
                    <Chip key={item.value} size="small" variant="outlined" label={localGuidanceText(item.label || fallbackText(item.value), isHindi)} />
                  ))}
                </Stack>
                {renderReasons(card.reasons)}
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2}>
          {timedCards.map((card) => (
            <Grid size={{ xs: 12, md: 4 }} key={localGuidanceText(card.title, false)}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%', borderRadius: 2, bgcolor: '#fffdf5' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  {localGuidanceText(card.title, isHindi)}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>{localGuidanceText(card.result, isHindi)}</Typography>
                <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                  {card.windows.map((window) => (
                    <Chip key={`${window.startAge}-${window.startYear}`} color={confidenceColor(window.confidence)} size="small" label={formatWindow(window)} />
                  ))}
                </Stack>
                {card.nature?.length ? renderReasons(card.nature) : renderReasons(card.reasons)}
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2, bgcolor: '#f8fbff' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            {localGuidanceText(concrete.wellness.title, isHindi)}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>{localGuidanceText(concrete.wellness.result, isHindi)}</Typography>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 1 }}>
            {concrete.wellness.cautionYears.map((window) => (
              <Chip key={`${window.startAge}-${window.startYear}`} color="warning" size="small" label={formatWindow(window)} />
            ))}
          </Stack>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{t('bnn.watchAreas')}</Typography>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
            {concrete.wellness.watchAreas.map((area, index) => (
              <Chip key={`${localGuidanceText(area, false)}-${index}`} size="small" variant="outlined" label={localGuidanceText(area, isHindi)} />
            ))}
          </Stack>
          {renderReasons(concrete.wellness.reasons)}
        </Paper>

        <Alert severity="info">{localGuidanceText(guidance.disclaimer, isHindi)}</Alert>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
          {t('bnn.guidanceTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('bnn.guidanceSubtitle')}
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {guidance.career.primaryFields.map((field, index) => (
          <Grid size={{ xs: 12, md: 6 }} key={`${localGuidanceText(field.field, false)}-${index}`}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%', borderRadius: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <StarIcon fontSize="small" color="warning" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {localGuidanceText(field.field, isHindi)}
                </Typography>
                <Chip
                  size="small"
                  color={confidenceColor(field.confidence)}
                  label={t(`bnn.guidanceConfidence.${field.confidence}`, { defaultValue: field.confidence })}
                />
              </Stack>
              <Stack spacing={0.75}>
                {field.reasons.map((reason, idx) => (
                  <Typography key={`${localGuidanceText(reason, false)}-${idx}`} variant="body2" color="text.secondary">
                    {localGuidanceText(reason, isHindi)}
                  </Typography>
                ))}
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        {guidance.topics.map((topic) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={topic.topic}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%', borderRadius: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                  {t(`bnn.guidanceTopics.${topic.topic}`, { defaultValue: topic.topic })}
                </Typography>
                <Chip
                  size="small"
                  color={confidenceColor(topic.strength)}
                  label={t(`bnn.guidanceConfidence.${topic.strength}`, { defaultValue: topic.strength })}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {t('bnn.karaka')}: {planetText(t, topic.karakaName)}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {localGuidanceText(topic.summary, isHindi)}
              </Typography>
              {topic.topConnections[0] && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {localGuidanceText(topic.topConnections[0].interpretation, isHindi)}
                </Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {t('bnn.eventGuidance')}
        </Typography>
        <Grid container spacing={2}>
          {guidance.eventGuidance.map((item) => (
            <Grid size={{ xs: 12, md: 6 }} key={`${item.topic}-${item.confidence}`}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fffdf5' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <TimelineIcon fontSize="small" color="warning" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {localGuidanceText(item.headline, isHindi)}
                  </Typography>
                  <Chip
                    size="small"
                    color={confidenceColor(item.confidence)}
                    label={t(`bnn.guidanceConfidence.${item.confidence}`, { defaultValue: item.confidence })}
                  />
                </Stack>
                <Typography variant="body2">{localGuidanceText(item.detail, isHindi)}</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  {item.transitTriggers.map((trigger, idx) => (
                    <Chip key={`${localGuidanceText(trigger, false)}-${idx}`} size="small" variant="outlined" label={localGuidanceText(trigger, isHindi)} />
                  ))}
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Alert severity="info">{localGuidanceText(guidance.disclaimer, isHindi)}</Alert>
    </Stack>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

const BNNAnalysis: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const { currentChart } = useChart();
  const [chartData, setChartData] = React.useState<any>(null);
  const [bnn, setBnn] = React.useState<BNNResult | null>(null);
  const [transit, setTransit] = React.useState<TransitResult | null>(null);
  const [guidance, setGuidance] = React.useState<BNNGuidance | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [isHoraryChart, setIsHoraryChart] = React.useState(false);

  const isPremiumUser = hasPremiumSubscription(authState.user);
  const isHindi = i18n.resolvedLanguage?.startsWith('hi') || i18n.language?.startsWith('hi');

  React.useEffect(() => {
    document.title = 'BNN Analysis - Kundlistar';
    if (id === 'preview') {
      if (currentChart) {
        setChartData(currentChart);
        setIsHoraryChart(currentChart?.chartType === 'horary');
        if (currentChart?.chartType === 'horary') {
          setLoading(false);
          return;
        }
        callBNN(null, currentChart);
      } else {
        setError(t('bnn.noPreviewData'));
        setLoading(false);
      }
    } else {
      fetchAndCalc();
    }
    // eslint-disable-next-line
  }, [id, currentChart]);

  const fetchAndCalc = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/charts/${id}`);
      const chart = response.data.data.chart;
      setChartData(chart);
      setIsHoraryChart(chart?.chartType === 'horary');
      if (chart?.chartType === 'horary') {
        setLoading(false);
        return;
      }
      await callBNN(id!, chart);
    } catch (err: any) {
      setError(t('bnn.failedLoad'));
    } finally {
      setLoading(false);
    }
  };

  const callBNN = async (chartId: string | null, rawChart: any) => {
    try {
      const body = chartId ? { chartId } : {
        planetData: buildPlanetData(rawChart),
        gender: rawChart?.birthDetails?.gender || 'male',
      };
      const res = await axios.post(`${API_BASE_URL}/bnn/calculate`, body);
      const bnnData = res.data.bnn;
      setBnn(bnnData);
      // Fetch transits using the same chartId (if available)
      if (isPremiumUser) {
        try {
          const transitBody = chartId ? { chartId } : { bnnResult: bnnData, gender: rawChart?.birthDetails?.gender || 'male' };
          const tRes = await axios.post(`${API_BASE_URL}/bnn/transits`, transitBody);
          setTransit(tRes.data);
        } catch {
          // Transits are non-critical; fail silently
        }
        try {
          const guidanceBody = chartId ? { chartId } : {
            planetData: buildPlanetData(rawChart),
            gender: rawChart?.birthDetails?.gender || 'male',
            birthDate: rawChart?.birthDetails?.dateOfBirth || rawChart?.dateOfBirth || rawChart?.dob,
          };
          const guidanceRes = await axios.post(`${API_BASE_URL}/bnn/guidance`, guidanceBody);
          setGuidance(guidanceRes.data.guidance);
        } catch (guidanceError) {
          console.warn('[BNN] Falling back to client guidance:', guidanceError);
          setGuidance(buildFallbackGuidance(bnnData, rawChart || chartData));
        }
      }
    } catch (err: any) {
      setError(t('bnn.failedCalculation'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (isHoraryChart) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, bgcolor: '#fafafa', minHeight: '100vh' }}>
        <AnalysisTabBar chartId={id!} active="bnn" isHoraryChart={true} />
        <Alert severity="info">{t('bnn.notAvailableForHorary')}</Alert>
      </Container>
    );
  }

  if (!bnn) return null;

  const directions = ['East', 'South', 'West', 'North'];

  return (
    <Container maxWidth="xl" sx={{ py: 4, bgcolor: '#fafafa', minHeight: '100vh' }}>
      <AnalysisTabBar chartId={id!} active="bnn" isHoraryChart={isHoraryChart} />

      {/* Header */}
      <Box
        sx={{
          mb: 3,
          textAlign: 'center',
          bgcolor: 'primary.main',
          p: 3,
          borderRadius: 2,
          border: '2px solid',
          borderColor: 'primary.dark',
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ color: 'white', fontWeight: 'bold' }}>
          {chartData?.birthDetails?.name || t('astrology.bnnAnalysis')}
        </Typography>
        <Typography variant="body1" color="rgba(255,255,255,0.9)">
          {t('bnn.subtitle')}
        </Typography>
        <Typography variant="caption" color="rgba(255,255,255,0.7)">
          {t('bnn.selfKaraka')}: <strong>{planetText(t, bnn.selfKaraka.name)}</strong>
        </Typography>
      </Box>

      <Stack spacing={3}>
        {bnn.bhriguPoint && (
          <Paper
            elevation={1}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: '#7c4dff55',
              bgcolor: '#f7f2ff',
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#4a148c' }}>
                  Bhrigu Point / Bhrigu Bindu
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Midpoint of Moon and Rahu
                </Typography>
              </Box>
              <Chip
                label={`${bnn.bhriguPoint.sign} ${formatDegreeMinutes(bnn.bhriguPoint.degreeInSign)}`}
                sx={{ bgcolor: '#7c4dff', color: '#fff', fontWeight: 600 }}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                {bnn.bhriguPoint.longitude.toFixed(4)}°
              </Typography>
            </Stack>
          </Paper>
        )}
        <DirectionalTransitChart
          bnn={bnn}
          transitSignMap={{}}
          title="Planet Directions - Without Retrograde Correction"
          referenceChartData={chartData}
        />
        <DirectionalTransitChart
          bnn={bnn}
          transitSignMap={{}}
          includeRetrogradeCorrections
          title="Planet Directions - With Retrograde Correction"
          referenceChartData={chartData}
        />
      </Stack>

      {/* ── Directional Groups Grid ── */}
      <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
        {t('bnn.directionalGroups')}
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {directions.map((dir) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={dir}>
            <DirectionCard
              direction={dir}
              data={bnn.directionalGroups[dir]}
              nadiStrings={bnn.nadiStrings}
            />
          </Grid>
        ))}
      </Grid>

      {/* ── Nadi Strings (Combinations) ── */}
      {bnn.nadiStrings.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {t('bnn.nadiCombinationStrings')}
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 4, borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('bnn.direction')}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('astrology.planet')}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('bnn.interpretationEnglish')}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('bnn.interpretationHinglish')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bnn.nadiStrings.map((ns, i) => {
                  const cfg = DIR_CONFIG[ns.direction];
                  return (
                    <TableRow key={i} sx={{ '&:nth-of-type(odd)': { bgcolor: cfg?.bg || 'inherit' } }}>
                      <TableCell>
                        <Chip
                          label={directionText(t, ns.direction)}
                          size="small"
                          sx={{ bgcolor: `${cfg?.color}22`, color: cfg?.color, fontWeight: 600, fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {ns.planets.map((pn, idx) => (
                            <React.Fragment key={pn}>
                              <Chip label={planetText(t, pn)} size="small" variant="outlined" />
                              {idx < ns.planets.length - 1 && <ArrowIcon sx={{ fontSize: 14, alignSelf: 'center', opacity: 0.5 }} />}
                            </React.Fragment>
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{translateFreeText(t, ns.interpretation.en)}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{translateFreeText(t, ns.interpretation.hi)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* ── Karaka-anchored Strings ── */}
      {bnn.karakaStrings.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {t('bnn.selfRelationshipStrings', { planet: planetText(t, bnn.selfKaraka.name) })}
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 4, borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'secondary.dark' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('astrology.planet')}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('bnn.relationship')}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('bnn.english')}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('bnn.hinglish')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bnn.karakaStrings.map((ks, i) => (
                  <TableRow key={i} sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}>
                    <TableCell>
                      <Chip label={planetText(t, ks.planetName)} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontStyle: 'italic' }}>{translateFreeText(t, ks.relationship)}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{translateFreeText(t, ks.interpretation.en)}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{translateFreeText(t, ks.interpretation.hi)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* ── Parivartana ── */}
      {bnn.parivartana.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {t('bnn.parivartana')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
            {bnn.parivartana.map((pv, i) => (
              <Paper key={i} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'warning.main', bgcolor: '#fffde7' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.dark' }}>{translateFreeText(t, pv.note)}</Typography>
              </Paper>
            ))}
          </Box>
        </>
      )}

      {/* ── Planet Table ── */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        {t('bnn.planetDegreeSummary')}
      </Typography>
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('astrology.planet')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('astrology.sign')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('bnn.degInSign')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('bnn.direction')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('bnn.retro')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bnn.planets.map((p, i) => {
              const dir = Object.entries(bnn.directionalGroups).find(([, d]) =>
                d.planets.some(dp => dp.key === p.key && !dp.isVirtual && !dp.retroInPrev)
              )?.[0] || '';
              const cfg = DIR_CONFIG[dir];
              return (
                <TableRow key={p.key} sx={{ '&:nth-of-type(odd)': { bgcolor: cfg?.bg || 'action.hover' } }}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{planetText(t, p.name)}</TableCell>
                  <TableCell>{t(`astrology.signs.${p.sign}`, { defaultValue: SIGN_NAMES[p.sign] })} ({p.sign})</TableCell>
                  <TableCell>{p.degInSign.toFixed(2)}°</TableCell>
                  <TableCell>
                    {dir && (
                      <Chip
                        label={directionText(t, dir)}
                        size="small"
                        sx={{ bgcolor: `${cfg?.color}22`, color: cfg?.color, fontWeight: 600, fontSize: '0.65rem' }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{yesNoRetro(t, p.isRetro)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Premium BNN Guidance ── */}
      <Paper elevation={1} sx={{ p: 3, mt: 4, borderRadius: 2, border: '2px solid #7c3aed30', bgcolor: '#ffffff' }}>
        {isPremiumUser ? (
          guidance ? (
            <BNNGuidanceSection guidance={guidance} isHindi={isHindi} />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('bnn.guidanceUnavailable')}
            </Typography>
          )
        ) : (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <LockIcon color="warning" sx={{ mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {t('bnn.unlockGuidance')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('bnn.upgradeGuidance')}
            </Typography>
            <Button variant="contained" color="warning" onClick={() => navigate('/upgrade')}>
              {t('common.upgrade')}
            </Button>
          </Box>
        )}
      </Paper>

      {/* ── Transit Analysis ── */}
      <Paper elevation={1} sx={{ p: 3, mt: 4, borderRadius: 2, border: '2px solid #f9a82540' }}>
        {isPremiumUser ? (
          transit ? (
            <TransitSection
              transit={transit}
              selfKaraka={bnn.selfKaraka}
              bnn={bnn}
              seoPath={id ? `/chart/${id}/bnn` : '/chart/preview/bnn'}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('bnn.transitDataUnavailable')}
            </Typography>
          )
        ) : (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {t('bnn.unlockTransit')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('bnn.upgradeTransit')}
            </Typography>
            <Button variant="contained" color="warning" onClick={() => navigate('/upgrade')}>
              {t('common.upgrade')}
            </Button>
          </Box>
        )}
      </Paper>

      {/* ── Progression Dashboard ── */}
      <Paper elevation={1} sx={{ p: 3, mt: 4, borderRadius: 2, border: '2px solid #f9a82520', bgcolor: '#fafafa' }}>
        {isPremiumUser ? (
          <BNNProgressionDashboard
            chartId={id !== 'preview' ? (id ?? null) : null}
            bnn={bnn}
            chartData={chartData}
          />
        ) : (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {t('bnn.unlockProgression')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('bnn.upgradeProgression')}
            </Typography>
            <Button variant="contained" color="warning" onClick={() => navigate('/upgrade')}>
              {t('common.upgrade')}
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

// Helper for preview mode (no chartId)
function buildPlanetData(chart: any): any[] {
  if (!chart?.planets) return [];
  return Object.entries(chart.planets).map(([key, p]: [string, any]) => ({
    name: key,
    sign: Math.floor(((p.longitude ?? 0) % 360) / 30) + 1,
    degInSign: (p.longitude ?? 0) % 30,
    isRetro: (p.speed ?? 0) < 0,
  }));
}

export default BNNAnalysis;
